import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { MibBreakpointService, timeToDuration } from '@medinbox/angular';
import { AnalyticsService } from '../../core/services/analytics.service';
import { AnalyticsEvents } from '../../core/models/analytics';

export type TabState = 'minimized' | 'fullscreen' | 'default';
export interface TabsState {
  video: TabState;
  chat: TabState;
  content: 'open' | 'closed';
}

const DEFAULT_TAB_STATE: TabsState = {
  video: 'minimized',
  chat: 'fullscreen',
  content: 'closed',
};

@Injectable({
  providedIn: 'root',
})
export class ChatPageState {
  private readonly analytics = inject(AnalyticsService);
  private readonly breakpointService = inject(MibBreakpointService);

  readonly isSmall = computed(() => this.breakpointService.isBaseOrSm());
  readonly currentSmallView = signal<'video' | 'chat' | 'content'>('chat');

  readonly tabCommonClasses = computed(() => `h-full md:rounded-2xl overflow-hidden`);
  readonly tabHeaderClasses =
    'h-17.5 max-lg:landscape:h-12 w-full flex items-center justify-between shrink-0';
  readonly tabState = signal<TabsState>(DEFAULT_TAB_STATE);
  readonly contentPanelOpen = computed(() => this.tabState().content === 'open');
  readonly contentDetailDialogOpen = signal(false);

  private readonly _timeClicked = signal<string>('');
  readonly timeInSeconds = computed<number | undefined>(() => {
    if (!this._timeClicked()) {
      return;
    }
    return timeToDuration(this._timeClicked());
  });
  private readonly _videoPaused = signal<boolean | null>(null);
  readonly videoPaused = this._videoPaused.asReadonly();

  constructor() {
    effect(() => {
      switch (this.tabState().video) {
        case 'minimized':
          this.analytics.trackEvent(AnalyticsEvents.LiveVideoTabClosed);
          break;
        case 'fullscreen':
          this.analytics.trackEvent(AnalyticsEvents.LiveVideoTabFullscreen);
          break;
        case 'default':
          this.analytics.trackEvent(AnalyticsEvents.LiveVideoTabOpen);
          break;
      }
    });
  }

  reset(): void {
    this._videoPaused.set(null);
    this._timeClicked.set('');
    this.tabState.set(DEFAULT_TAB_STATE);
    this.currentSmallView.set('video');
    this.contentDetailDialogOpen.set(false);
  }

  toggleChat(): void {
    this.tabState.update((tabs) => {
      switch (tabs.chat) {
        case 'default':
          return {
            chat: 'minimized',
            video: 'fullscreen',
            content: tabs.content,
          };
        case 'minimized':
        case 'fullscreen':
          return {
            chat: 'default',
            video: 'default',
            content: tabs.content,
          };
      }
    });
  }

  toggleVideo(): void {
    this.tabState.update((tabs) => {
      const isMdBreakpoint = this.breakpointService.current() === 'md';
      const isContentOpen = tabs.content === 'open';

      switch (tabs.video) {
        case 'default':
          return {
            chat: 'fullscreen',
            video: 'minimized',
            content: tabs.content,
          };
        case 'minimized':
        case 'fullscreen':
          // Close content panel when minimizing video at md breakpoint
          return {
            chat: 'default',
            video: 'default',
            content: isMdBreakpoint && isContentOpen ? 'closed' : tabs.content,
          };
      }
    });
  }

  toggleContentTab(): void {
    this.tabState.update((tabs) => {
      const isOpeningContent = tabs.content === 'closed';
      const isMdBreakpoint = this.breakpointService.current() === 'md';

      // Close chat panel when opening content panel at md breakpoint
      if (isOpeningContent && isMdBreakpoint) {
        return {
          ...tabs,
          content: 'open',
          chat: 'minimized',
          video: 'fullscreen',
        };
      }

      return {
        ...tabs,
        content: tabs.content === 'open' ? 'closed' : 'open',
      };
    });

    this.currentSmallView.update((smallView) => (smallView === 'content' ? 'video' : 'content'));
  }

  clickOnTimeLink(ts: string): void {
    this.currentSmallView.set('video');
    this._timeClicked.set(ts);
    this._videoPaused.set(false);
  }

  pauseVideo(): void {
    this._videoPaused.set(true);
  }
}
